import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../ui/use-toast';
import { useNoshStore } from '../../lib/store';
import { 
  ArrowRight, 
  ArrowLeft, 
  User, 
  Heart, 
  AlertTriangle, 
  Scale, 
  Target,
  CheckCircle,
  X
} from 'lucide-react';

// Import dietary and allergy options from Profile.tsx patterns
const dietaryOptions = [
  { 
    value: 'balanced', 
    label: 'Balanced Diet', 
    icon: '🍽️', 
    color: 'bg-slate-50 dark:bg-slate-900/20 border-slate-200 dark:border-slate-800/30', 
    iconBg: 'bg-slate-100 dark:bg-slate-800/30', 
    textColor: 'text-slate-700 dark:text-slate-300'
  },
  { 
    value: 'vegetarian', 
    label: 'Vegetarian', 
    icon: '🥬', 
    color: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/30', 
    iconBg: 'bg-green-100 dark:bg-green-800/30', 
    textColor: 'text-green-700 dark:text-green-300'
  },
  { 
    value: 'vegan', 
    label: 'Vegan', 
    icon: '🌱', 
    color: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/30', 
    iconBg: 'bg-emerald-100 dark:bg-emerald-800/30', 
    textColor: 'text-emerald-700 dark:text-emerald-300'
  },
  { 
    value: 'pescatarian', 
    label: 'Pescatarian', 
    icon: '🐟', 
    color: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800/30', 
    iconBg: 'bg-cyan-100 dark:bg-cyan-800/30', 
    textColor: 'text-cyan-700 dark:text-cyan-300'
  },
  { 
    value: 'keto', 
    label: 'Keto / Low-Carb', 
    icon: '🥓', 
    color: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800/30', 
    iconBg: 'bg-purple-100 dark:bg-purple-800/30', 
    textColor: 'text-purple-700 dark:text-purple-300'
  },
  { 
    value: 'halal', 
    label: 'Halal', 
    icon: '☪️', 
    color: 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800/30', 
    iconBg: 'bg-teal-100 dark:bg-teal-800/30', 
    textColor: 'text-teal-700 dark:text-teal-300'
  },
  { 
    value: 'kosher', 
    label: 'Kosher', 
    icon: '✡️', 
    color: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800/30', 
    iconBg: 'bg-indigo-100 dark:bg-indigo-800/30', 
    textColor: 'text-indigo-700 dark:text-indigo-300'
  },
  { 
    value: 'gluten-free', 
    label: 'Gluten-Free', 
    icon: '🌾', 
    color: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/30', 
    iconBg: 'bg-amber-100 dark:bg-amber-800/30', 
    textColor: 'text-amber-700 dark:text-amber-300'
  },
  { 
    value: 'paleo', 
    label: 'Paleo', 
    icon: '🦴', 
    color: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/30', 
    iconBg: 'bg-orange-100 dark:bg-orange-800/30', 
    textColor: 'text-orange-700 dark:text-orange-300'
  }
];

const allergyOptions = [
  { value: 'nuts', label: 'Nuts', icon: '🥜', color: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/30', iconBg: 'bg-red-100 dark:bg-red-800/30', textColor: 'text-red-700 dark:text-red-300' },
  { value: 'shellfish', label: 'Shellfish', icon: '🦐', color: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/30', iconBg: 'bg-orange-100 dark:bg-orange-800/30', textColor: 'text-orange-700 dark:text-orange-300' },
  { value: 'eggs', label: 'Eggs', icon: '🥚', color: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800/30', iconBg: 'bg-yellow-100 dark:bg-yellow-800/30', textColor: 'text-yellow-700 dark:text-yellow-300' },
  { value: 'soy', label: 'Soy', icon: '🫘', color: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800/30', iconBg: 'bg-purple-100 dark:bg-purple-800/30', textColor: 'text-purple-700 dark:text-purple-300' },
  { value: 'wheat', label: 'Wheat', icon: '🌾', color: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/30', iconBg: 'bg-amber-100 dark:bg-amber-800/30', textColor: 'text-amber-700 dark:text-amber-300' },
  { value: 'fish', label: 'Fish', icon: '🐠', color: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800/30', iconBg: 'bg-cyan-100 dark:bg-cyan-800/30', textColor: 'text-cyan-700 dark:text-cyan-300' },
  { value: 'milk', label: 'Milk', icon: '🥛', color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/30', iconBg: 'bg-blue-100 dark:bg-blue-800/30', textColor: 'text-blue-700 dark:text-blue-300' },
  { value: 'peanuts', label: 'Peanuts', icon: '🥜', color: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800/30', iconBg: 'bg-rose-100 dark:bg-rose-800/30', textColor: 'text-rose-700 dark:text-rose-300' }
];

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const OnboardingModal = ({ isOpen, onClose }: OnboardingModalProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { updateUserName, updateUserPreferences, updateUserAllergies, updateBodyStats, setOnboardingComplete, user } = useNoshStore();

  // Form state - controlled without auto-save to prevent focus loss
  const [formData, setFormData] = useState({
    name: '',
    dietaryPreferences: [] as string[],
    allergies: [] as string[],
    age: '',
    gender: '' as 'male' | 'female' | 'other' | '',
    height: '',
    weight: '',
    targetWeight: '',
    goal: '' as 'lose' | 'maintain' | 'gain' | '',
    activityLevel: '' as 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active' | ''
  });

  const totalSteps = 6;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle mobile navigation hiding
  useEffect(() => {
    if (isOpen && isMobile) {
      const mobileNav = document.querySelector('.mobile-navigation');
      if (mobileNav) {
        (mobileNav as HTMLElement).style.transform = 'translateY(100%)';
        (mobileNav as HTMLElement).style.transition = 'transform 0.3s ease-in-out';
      }
    }

    return () => {
      if (isMobile) {
        const mobileNav = document.querySelector('.mobile-navigation');
        if (mobileNav) {
          (mobileNav as HTMLElement).style.transform = 'translateY(0)';
          (mobileNav as HTMLElement).style.transition = 'transform 0.3s ease-in-out';
        }
      }
    };
  }, [isOpen, isMobile]);

  const handleNext = async () => {
    setIsLoading(true);
    
    try {
      // Save current step data before moving to next
      switch (currentStep) {
        case 1:
          if (!formData.name.trim()) {
            toast({
              title: "Name required",
              description: "Please enter your name to continue.",
              variant: "destructive"
            });
            setIsLoading(false);
            return;
          }
          await updateUserName(formData.name.trim());
          break;
          
        case 2:
          // Dietary preferences - allow empty selection
          await updateUserPreferences(formData.dietaryPreferences);
          break;
          
        case 3:
          // Allergies - allow empty selection
          await updateUserAllergies(formData.allergies);
          break;
          
        case 4:
          // Basic body stats validation
          if (!formData.age || !formData.gender) {
            toast({
              title: "Basic info required",
              description: "Please provide your age and gender.",
              variant: "destructive"
            });
            setIsLoading(false);
            return;
          }
          break;
          
        case 5:
          // Body measurements and goals validation
          if (!formData.height || !formData.weight || !formData.goal || !formData.activityLevel) {
            toast({
              title: "Complete your profile",
              description: "Please fill in all body stats and goals.",
              variant: "destructive"
            });
            setIsLoading(false);
            return;
          }
          
          // Convert measurements to metric if user is using imperial
          const isMetric = user.measurementSystem === 'metric';
          let heightInCm = parseFloat(formData.height);
          let weightInKg = parseFloat(formData.weight);
          let targetWeightInKg = formData.targetWeight ? parseFloat(formData.targetWeight) : undefined;
          
          if (!isMetric) {
            // Convert imperial to metric for storage
            // Height: Handle feet'inches" format (e.g., "5'8") or decimal feet
            if (formData.height.includes("'")) {
              const [feet, inches] = formData.height.split("'").map(s => parseFloat(s.replace('"', '')));
              heightInCm = (feet * 12 + (inches || 0)) * 2.54;
            } else {
              // Assume decimal feet
              heightInCm = parseFloat(formData.height) * 30.48;
            }
            
            // Weight: lbs to kg
            weightInKg = parseFloat(formData.weight) * 0.453592;
            
            // Target weight: lbs to kg
            if (targetWeightInKg) {
              targetWeightInKg = targetWeightInKg * 0.453592;
            }
          }
          
          // Save all body stats at once (always in metric for database)
          await updateBodyStats({
            age: parseInt(formData.age),
            gender: formData.gender || undefined,
            height: heightInCm,
            weight: weightInKg,
            targetWeight: targetWeightInKg,
            goal: formData.goal || undefined,
            activityLevel: formData.activityLevel || undefined
          });
          break;
      }

      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        // Complete onboarding
        setOnboardingComplete();
        toast({
          title: "Welcome to Nosh! 🎉",
          description: "Your profile is now complete. Let's start reducing food waste!",
        });
        onClose();
      }
    } catch (error) {
      console.error('Error saving onboarding step:', error);
      toast({
        title: "Error saving data",
        description: "Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Pill-style preference card component
  const PreferenceCard = ({ 
    option, 
    isSelected, 
    onToggle
  }: { 
    option: any, 
    isSelected: boolean, 
    onToggle: () => void
  }) => (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onToggle}
      className={`relative p-3 rounded-2xl border-2 transition-all duration-200 text-left w-full ${
        isSelected
          ? option.color + ' border-transparent shadow-md'
          : 'bg-gray-100 dark:bg-gray-600 border-transparent shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-500'
      }`}
    >
      <div className="flex items-center space-x-2 sm:space-x-3">
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
          isSelected ? option.iconBg : 'bg-gray-100 dark:bg-gray-700'
        }`}>
          <span className="text-base sm:text-lg">{option.icon}</span>
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold text-xs sm:text-sm transition-colors duration-200 leading-tight ${
            isSelected ? option.textColor : 'text-gray-900 dark:text-gray-100'
          }`}>
            {option.label}
          </h3>
        </div>
        
        <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 flex-shrink-0 ${
          isSelected 
            ? 'bg-white border-white shadow-sm' 
            : 'border-gray-300 dark:border-gray-600'
        }`}>
          {isSelected && (
            <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full" />
          )}
        </div>
      </div>
      
      {isSelected && (
        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl pointer-events-none" />
      )}
    </motion.button>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <User className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Welcome to Nosh!</h2>
                <p className="text-muted-foreground">Let's set up your profile to get personalized recommendations</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">What should we call you?</label>
                <Input
                  type="text"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                <Heart className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Dietary Preferences</h2>
                <p className="text-muted-foreground">Select your dietary lifestyle (optional)</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
              {dietaryOptions.map((diet) => (
                <PreferenceCard
                  key={diet.value}
                  option={diet}
                  isSelected={formData.dietaryPreferences.includes(diet.value)}
                  onToggle={() => {
                    setFormData(prev => ({
                      ...prev,
                      dietaryPreferences: prev.dietaryPreferences.includes(diet.value)
                        ? []
                        : [diet.value]
                    }));
                  }}
                />
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto">
                <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Allergies & Restrictions</h2>
                <p className="text-muted-foreground">You can add additional custom allergies later in settings</p>
               
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
              {allergyOptions.map((allergy) => (
                <PreferenceCard
                  key={allergy.value}
                  option={allergy}
                  isSelected={formData.allergies.includes(allergy.value)}
                  onToggle={() => {
                    setFormData(prev => ({
                      ...prev,
                      allergies: prev.allergies.includes(allergy.value)
                        ? prev.allergies.filter(a => a !== allergy.value)
                        : [...prev.allergies, allergy.value]
                    }));
                  }}
                />
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto">
                <User className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Basic Information</h2>
                <p className="text-muted-foreground">This helps us calculate your nutrition needs</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Age</label>
                <Input
                  type="number"
                  placeholder="Enter your age"
                  value={formData.age}
                  onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                  min="13"
                  max="120"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Gender</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                    { value: 'other', label: 'Other' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setFormData(prev => ({ ...prev, gender: option.value as any }))}
                      className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                        formData.gender === option.value
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:border-primary/50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        // Get measurement system labels based on user's auto-detected system
        const isMetric = user.measurementSystem === 'metric';
        const heightLabel = isMetric ? 'Height (cm)' : 'Height (ft\'in")';
        const weightLabel = isMetric ? 'Weight (kg)' : 'Weight (lbs)';
        const targetWeightLabel = isMetric ? 'Target Weight (kg) - Optional' : 'Target Weight (lbs) - Optional';
        const heightPlaceholder = isMetric ? '170' : '5\'8"';
        const weightPlaceholder = isMetric ? '70' : '154';
        const targetWeightPlaceholder = isMetric ? '65' : '143';
        
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto">
                <Scale className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Body Stats & Goals</h2>
                <p className="text-muted-foreground">Help us personalize your nutrition targets</p>
                <p className="text-xs text-muted-foreground mt-1">
                  📍 Using {isMetric ? 'Metric' : 'Imperial'} units (auto-detected from your location)
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">{heightLabel}</label>
                  <Input
                    type={isMetric ? "number" : "text"}
                    placeholder={heightPlaceholder}
                    value={formData.height}
                    onChange={(e) => setFormData(prev => ({ ...prev, height: e.target.value }))}
                    min={isMetric ? "100" : undefined}
                    max={isMetric ? "250" : undefined}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">{weightLabel}</label>
                  <Input
                    type="number"
                    placeholder={weightPlaceholder}
                    value={formData.weight}
                    onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                    min={isMetric ? "30" : "66"}
                    max={isMetric ? "300" : "660"}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{targetWeightLabel}</label>
                <Input
                  type="number"
                  placeholder={targetWeightPlaceholder}
                  value={formData.targetWeight}
                  onChange={(e) => setFormData(prev => ({ ...prev, targetWeight: e.target.value }))}
                  min={isMetric ? "30" : "66"}
                  max={isMetric ? "300" : "660"}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Fitness Goal</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'lose', label: 'Lose Weight', emoji: '📉', desc: 'Reduce body weight' },
                    { value: 'maintain', label: 'Maintain', emoji: '⚖️', desc: 'Stay at current weight' },
                    { value: 'gain', label: 'Gain Muscle', emoji: '💪', desc: 'Build muscle mass' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setFormData(prev => ({ ...prev, goal: option.value as any }))}
                      className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm ${
                        formData.goal === option.value
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="text-lg mb-1">{option.emoji}</div>
                      <div className="font-medium">{option.label}</div>
                      <div className={`text-xs mt-1 ${
                        formData.goal === option.value ? 'text-primary-foreground/80' : 'text-muted-foreground'
                      }`}>
                        {option.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Activity Level</label>
                <div className="space-y-2">
                  {[
                    { value: 'sedentary', label: 'Sedentary', desc: 'Little to no exercise' },
                    { value: 'light', label: 'Light', desc: 'Light exercise 1-3 days/week' },
                    { value: 'moderate', label: 'Moderate', desc: 'Moderate exercise 3-5 days/week' },
                    { value: 'active', label: 'Active', desc: 'Hard exercise 6-7 days/week' },
                    { value: 'very_active', label: 'Very Active', desc: 'Very hard exercise, physical job' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setFormData(prev => ({ ...prev, activityLevel: option.value as any }))}
                      className={`w-full p-3 rounded-lg border-2 transition-all duration-200 text-left ${
                        formData.activityLevel === option.value
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="font-medium">{option.label}</div>
                      <div className={`text-sm ${
                        formData.activityLevel === option.value ? 'text-primary-foreground/80' : 'text-muted-foreground'
                      }`}>
                        {option.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">You're All Set! 🎉</h2>
                <p className="text-muted-foreground">Your profile is complete and we'll now provide personalized recommendations</p>
              </div>
            </div>
            
            {/* Free Recipes Highlight */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6 border border-green-200 dark:border-green-800/30">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-800/30 rounded-full flex items-center justify-center">
                  <span className="text-lg">🍽️</span>
                </div>
                <div>
                  <h3 className="font-semibold text-green-800 dark:text-green-200">Welcome Gift! 🎁</h3>
                  <p className="text-sm text-green-700 dark:text-green-300">We've added some delicious starter recipes to your collection</p>
                </div>
              </div>
              <p className="text-sm text-green-600 dark:text-green-400 leading-relaxed">
                Explore your recipe collection to discover tasty meals. These free recipes are ready to cook and perfectly suited to help you get started on your culinary journey! 🌟
              </p>
            </div>
            
            <div className="bg-muted/50 rounded-2xl p-6 space-y-4">
              <h3 className="font-semibold text-foreground">What's next?</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Check out your free starter recipes 📚</li>
                <li>• Add items to your inventory 📦</li>
                <li>• Get personalized recipe suggestions 🤖</li>
                <li>• Track your nutrition goals 📊</li>
                <li>• Reduce food waste with smart alerts ⚡</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  // Mobile full-screen layout
  if (isMobile) {
    return (
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.3 }}
        className="fixed inset-0 bg-background z-50 overflow-y-auto"
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain'
        }}
      >
        <div className="min-h-full flex flex-col">
          {/* Header */}
          <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {currentStep > 1 && (
                  <button
                    onClick={handlePrevious}
                    className="p-2 hover:bg-muted rounded-full transition-colors"
                  >
                    <ArrowLeft size={20} className="text-muted-foreground" />
                  </button>
                )}
                <div>
                  <h1 className="text-lg font-semibold text-foreground">Setup Profile</h1>
                  <p className="text-sm text-muted-foreground">Step {currentStep} of {totalSteps}</p>
                </div>
              </div>
              
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted rounded-full transition-colors"
              >
                <X size={20} className="text-muted-foreground" />
              </button>
            </div>
            
            {/* Progress bar */}
            <div className="mt-4">
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm border-t border-border p-4">
            <div className="flex justify-between space-x-3">
              {currentStep > 2 && currentStep < 6 && (
                <Button
                  variant="outline"
                  onClick={handleSkip}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Skip
                </Button>
              )}
              
              <Button
                onClick={handleNext}
                disabled={isLoading}
                className="flex-1"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-primary-foreground/70 border-t-transparent rounded-full animate-spin" />
                    <span>Saving...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span>{currentStep === totalSteps ? 'Get Started' : 'Next'}</span>
                    <ArrowRight size={16} />
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Desktop modal layout
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-background rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="border-b border-border p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-foreground">Setup Your Profile</h1>
                <p className="text-sm text-muted-foreground">Step {currentStep} of {totalSteps}</p>
              </div>
              
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted rounded-full transition-colors"
              >
                <X size={20} className="text-muted-foreground" />
              </button>
            </div>
            
            {/* Progress bar */}
            <div className="mt-4">
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="border-t border-border p-6">
            <div className="flex justify-between space-x-3">
              <div className="flex space-x-3">
                {currentStep > 1 && (
                  <Button
                    variant="outline"
                    onClick={handlePrevious}
                    disabled={isLoading}
                  >
                    <ArrowLeft size={16} className="mr-2" />
                    Previous
                  </Button>
                )}
                
                {currentStep > 2 && currentStep < 6 && (
                  <Button
                    variant="ghost"
                    onClick={handleSkip}
                    disabled={isLoading}
                  >
                    Skip
                  </Button>
                )}
              </div>
              
              <Button
                onClick={handleNext}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-primary-foreground/70 border-t-transparent rounded-full animate-spin" />
                    <span>Saving...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span>{currentStep === totalSteps ? 'Get Started' : 'Next'}</span>
                    <ArrowRight size={16} />
                  </div>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}; 